import {
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  type PointsHistoryWidgetProps,
  type PointsHistoryThemingProps,
  POINTS_HISTORY_THEMING_PROP_TO_PARAM,
} from "./types";

/**
 * Ref handle exposed by LegionPointsHistory for imperative control.
 */
export interface LegionPointsHistoryHandle {
  /** Reload the widget iframe */
  refresh: () => void;
}

/**
 * Build the iframe src URL from widget props.
 */
export function buildPointsHistoryWidgetUrl(
  props: PointsHistoryWidgetProps
): string {
  const { baseUrl, theme = "light", showClose } = props;

  const url = new URL("/widget/points-history", baseUrl);
  url.searchParams.set("theme", theme);
  if (showClose) url.searchParams.set("showClose", "true");

  // Append theming params (camelCase param names matching host expectations)
  const themingKeys = Object.keys(
    POINTS_HISTORY_THEMING_PROP_TO_PARAM
  ) as Array<keyof PointsHistoryThemingProps>;
  for (const key of themingKeys) {
    const value = props[key];
    if (value !== undefined && value !== null && String(value).length > 0) {
      url.searchParams.set(
        POINTS_HISTORY_THEMING_PROP_TO_PARAM[key],
        String(value)
      );
    }
  }

  return url.toString();
}

/**
 * LegionPointsHistory – a React component that embeds the Legion
 * Points History widget via an iframe.
 *
 * @example
 * ```tsx
 * import { LegionPointsHistory } from '@legionhandtech/lht-react-widgets';
 *
 * function App() {
 *   return (
 *     <LegionPointsHistory
 *       baseUrl="https://your-legion-domain.com"
 *       authToken="your-jwt-token"
 *       theme="light"
 *     />
 *   );
 * }
 * ```
 */
export const LegionPointsHistory = forwardRef<
  LegionPointsHistoryHandle,
  PointsHistoryWidgetProps
>(function LegionPointsHistory(props, ref) {
  const {
    theme = "light",
    authToken,
    showClose,
    width = "100%",
    height = "600px",
    className,
    style,
    onLoad,
    onError,
    baseUrl,
    ...rest
  } = props;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        const iframe = iframeRef.current;
        if (iframe?.contentWindow) {
          iframe.contentWindow.location.reload();
        }
      },
    }),
    []
  );

  const src = useMemo(
    () => buildPointsHistoryWidgetUrl(props),
    [
      baseUrl,
      theme,
      showClose,
      ...Object.keys(POINTS_HISTORY_THEMING_PROP_TO_PARAM).map(
        (k) => rest[k as keyof typeof rest]
      ),
    ]
  );

  const handleLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (authToken && iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        { type: "AUTH_TOKEN", token: authToken },
        baseUrl
      );
    }
    onLoad?.({ widget: "points-history", theme });
  }, [onLoad, theme, authToken, baseUrl]);

  const handleError = useCallback(() => {
    onError?.("Failed to load points history widget");
  }, [onError]);

  const containerStyle: React.CSSProperties = {
    display: "block",
    width,
    backgroundColor: "transparent",
    ...style,
  };

  const iframeStyle: React.CSSProperties = {
    border: "none",
    overflow: "hidden",
    width: "100%",
    height,
  };

  return (
    <div
      className={className}
      style={containerStyle}
      data-testid="legion-points-history"
    >
      <iframe
        ref={iframeRef}
        src={src}
        title="Points History Widget"
        width="100%"
        loading="lazy"
        style={iframeStyle}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
});
